/**
 * CopilotDock — the embedded chat surface (the studio pages' "Ask Vee"): the
 * SAME ChatContext conversation as the AI Agent page, at FULL feature parity
 * with it:
 *
 *   - ask-cards docked above the composer (details confirm → facts form →
 *     post-draft placeholder FILL → web-verify offer), same priority order
 *   - session companyOverrides (shared localStorage mirror + hold-back ack)
 *   - quoted document passages ("Ask Vee about this" on the studio canvas)
 *   - queue-while-streaming sends, edit & resend with ‹x/y› versions, branch
 *     to a new chat, thumbs feedback, real agent-action confirmation
 *   - web-search toggle + one-tap allow on Vee's verify offers
 *   - in-dock scoped history + New chat; origin-aware arrivals (openSessionId)
 *
 * The host page passes getPageContext/onClientActions/onArtifact — the dock
 * owns no page side effects itself.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiX, FiPlus, FiClock, FiArrowLeft, FiFileText, FiExternalLink, FiGlobe, FiPaperclip } from 'react-icons/fi';
import { ROUTES } from '../constants/constants';
import { aiAPI } from '../services/api';
import { saveChatImage, getChatImage } from '../services/chatMediaCache';
import { useChat, hydratePersistedMessages, lastSessionKey } from '../context/ChatContext';
import {
  MAX_ATTACHMENTS, ATTACH_ACCEPT, ATTACH_EXT, ATTACH_TYPES_LABEL, uid, CHAT_ATTACH_MAX_CHARS, MAX_ATTACH_BYTES, MAX_ATTACH_MB,
  imageToBase64, localFileType, AttachmentChip, AttachmentPreview,
} from './attachments';
import { useApp } from '../context/AppContext';
import { useUI } from '../context/UIContext';
import ChatThread from './ChatThread';
import ChatComposer from './ChatComposer';
import { useChatTurn } from './useChatTurn';
import { buildWireHistory } from './wire';
import {
  DetailsConfirmCard, FactsFormCard, WebSearchOfferCard, WEB_VERIFY_OFFER_RE, placeholderLabel,
} from './cards';
import {
  withPrefilledValues, attachPersonOptions, attachCompanyOptions, pickedCompanyFrom,
  companyChoiceSentence, threadCompanyFrom,
} from './placeholderPrefill';
import {
  buildAskLedger, pruneSettledFields, carryForwardValues, supersededByChatPatch,
  confirmedCompanies, isCompanyConfirmSettled, askDraftKind, askBelongsToSurface,
} from './askLedger';
import { isSameDraft, applyPlaceholderFill } from '../utils/draftUtils';
import { minutesPatchFromPlaceholders } from '../utils/factCarryForward';

// Compact relative time for the in-dock history rows.
const timeAgo = (iso) => {
  const t = iso ? new Date(iso).getTime() : NaN;
  if (Number.isNaN(t)) return '';
  const s = Math.floor((Date.now() - t) / 1000);
  if (s < 60) return 'just now';
  const m = Math.floor(s / 60); if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60); if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24); if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
};

export default function CopilotDock({
  getPageContext, onClientActions, onArtifact, onClose, title = 'Ask Vee',
  // Scope of the IN-DOCK history list ('minutes' on the Minutes studio). The
  // full AI-chat history keeps every conversation regardless.
  historyDraftKind = null,
  // A session to open on arrival (origin-aware routing). Consumed once.
  openSessionId = null,
  // A passage the user selected on the host page's document — rendered as a
  // context chip and prepended to the next send (Copilot-style quoting).
  quote = null,
  onClearQuote,
  // Host-page voice: the first-open hint and composer placeholder default to
  // the Minutes studio's copy; other studios pass their own.
  emptyHint = null,
  composerPlaceholder = null,
  // A prompt the HOST wants sent as if the user typed it (e.g. the Details
  // panel's "let Vee fill the gaps"). Each distinct object is sent once.
  autoSend = null,
  onAutoSent,
}) {
  const navigate = useNavigate();
  const { activeCompany, companies, fetchCompanies } = useApp();
  const { showToast } = useUI();
  const {
    messages, setMessages, loading, sessionId, setSessionId,
    consentPersist, consentTrain,
    sendTurn, stopGenerating, resetChat, markForked, registerPageHandlers,
    conversationOrigin, setConversationOrigin,
  } = useChat();

  // ── Cross-studio isolation ───────────────────────────────────────────────
  // Agent-origin conversations are shared everywhere (the "same chat as the
  // AI Agent page" contract), but this dock must never adopt the OTHER
  // studio's conversation: arriving on the Report studio with the Minutes
  // studio's chat live (or vice versa) quietly starts a fresh one — the old
  // conversation is already saved in History and reopens on ITS OWN page.
  const myPage = historyDraftKind === 'minutes' ? 'minutes' : historyDraftKind === 'report' ? 'report' : null;
  const isForeignOrigin = (o) => ['minutes', 'report'].includes(o) && o !== myPage;
  // Isolation without amnesia: a foreign chat is swapped for THIS surface's own
  // last conversation when there is one, so reopening the studio continues
  // where you left off here instead of staring at a blank dock.
  const resumeOwnOrReset = () => {
    let mine = null;
    try { mine = localStorage.getItem(lastSessionKey(myPage)); } catch { /* ignore */ }
    if (mine && mine !== sessionId) { openSession(mine, { quiet: true }); return; }
    resetChat();
  };

  useEffect(() => {
    if (!myPage || openSessionId) return undefined; // explicit arrivals load their own session
    if (loading) return undefined; // never cut off a mid-stream reply
    if (isForeignOrigin(conversationOrigin)) { resumeOwnOrReset(); return undefined; }
    // Empty dock on arrival (fresh load, or a foreign chat was reset on the
    // way out) → continue this surface's last conversation. New chat clears
    // the key, so a deliberately fresh start stays fresh.
    if (!sessionId && !messages.some((m) => m.role === 'user')) {
      let mine = null;
      try { mine = localStorage.getItem(lastSessionKey(myPage)); } catch { /* ignore */ }
      if (mine) openSession(mine, { quiet: true });
      return undefined;
    }
    // Origin UNKNOWN but the conversation is real and saved. This is the
    // common case in practice: the chat predates origin tracking, or the
    // device-local mirror was lost while the transcript survived a reload.
    // The server stamped `origin` on the session's first turn, so it is the
    // authority — ask it, remember the answer, and apply the same rule.
    let cancelled = false;
    if (!conversationOrigin && sessionId && messages.some((m) => m.role === 'user')) {
      aiAPI.getChatSession(sessionId)
        .then((res) => {
          if (cancelled) return;
          const origin = (res?.data?.session || res?.session || {}).origin || 'agent';
          setConversationOrigin(origin);
          if (isForeignOrigin(origin)) resumeOwnOrReset();
        })
        .catch(() => { /* offline / deleted session: leave the conversation alone */ });
    }
    return () => { cancelled = true; };
    // Mount-time decision only — origin changes after mount are this dock's own doing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 'chat' | 'history' — History renders IN the dock (scoped to this page's
  // document kind); the full unfiltered browser stays on the AI Agent page.
  const [view, setView] = useState('chat');
  const [histSessions, setHistSessions] = useState(null);
  const [histLoading, setHistLoading] = useState(false);

  const [input, setInput] = useState('');
  // Compose-while-streaming: prompts sent mid-generation queue and auto-flush.
  const [queuedText, setQueuedText] = useState(null);
  // Quote chip excerpt expand/collapse (matches the Agent page's chip).
  const [quoteExpanded, setQuoteExpanded] = useState(false);

  // ── Attachments (images seen by vision; PDF/DOCX/TXT — e.g. a signed
  // resolution or DIN letter — extracted to text in-memory, never persisted).
  // Same pipeline as the AI Agent page, via the shared chat/attachments core.
  const [attachments, setAttachments] = useState([]);
  const [previewId, setPreviewId] = useState(null);
  // Lightbox over a SENT turn's attachments (history — no Remove), separate
  // from `previewId` which previews what's still sitting in the composer.
  const [sentPreview, setSentPreview] = useState(null);
  const fileInputRef = useRef(null);
  const attachmentsRef = useRef(attachments);
  useEffect(() => { attachmentsRef.current = attachments; }, [attachments]);
  useEffect(() => () => {
    attachmentsRef.current.forEach((a) => { if (a.previewUrl) URL.revokeObjectURL(a.previewUrl); });
  }, []);

  const patchAttachment = (id, patch) =>
    setAttachments((prev) => prev.map((a) => (a.id === id ? { ...a, ...patch } : a)));

  const extractAttachment = async (id, file, fileType) => {
    try {
      if (fileType === 'image') {
        const { mimeType, data, dataUrl } = await imageToBase64(file);
        patchAttachment(id, { status: 'ready', kind: 'image', mimeType, data, dataUrl });
        return;
      }
      const res = await aiAPI.extractSupportingDocuments([file], { maxChars: CHAT_ATTACH_MAX_CHARS });
      const doc = (res?.data?.documents || res?.documents || [])[0];
      const text = String(doc?.extractedText || '').trim();
      if (!doc || doc.error || !text) {
        patchAttachment(id, { status: 'error', error: doc?.error || 'No readable text found' });
      } else {
        patchAttachment(id, { status: 'ready', kind: 'text', text, truncated: !!doc.truncated });
      }
    } catch (err) {
      // Keep the real reason — friendlyAttachError maps it to a readable line.
      // 413 arrives from nginx as an HTML page, so the status is the only signal.
      const tooBig = err?.statusCode === 413 || err?.status === 413;
      patchAttachment(id, {
        status: 'error',
        error: tooBig ? `File is too large to upload (max ${MAX_ATTACH_MB} MB)` : (err?.message || 'Could not read this file'),
      });
    }
  };

  const addAttachments = (fileList) => {
    const all = Array.from(fileList || []);
    // Reject oversized files here rather than letting them fail mid-upload.
    const supported = all.filter((f) => ATTACH_EXT.test(f.name || ''));
    if (supported.length < all.length) showToast(`Unsupported file — use ${ATTACH_TYPES_LABEL}`, 'warning');
    supported
      .filter((f) => (f.size || 0) > MAX_ATTACH_BYTES)
      .forEach((f) => showToast(`${f.name} is too large — max ${MAX_ATTACH_MB} MB`, 'warning'));
    const picked = supported.filter((f) => (f.size || 0) <= MAX_ATTACH_BYTES);
    if (!picked.length) return;
    const room = MAX_ATTACHMENTS - attachments.length;
    if (room <= 0) { showToast(`You can attach up to ${MAX_ATTACHMENTS} files`, 'info'); return; }
    const take = picked.slice(0, room);
    const entries = take.map((f) => {
      const fileType = localFileType(f);
      return {
        id: uid(),
        name: f.name,
        fileType,
        size: f.size || 0,
        previewUrl: fileType === 'image' ? URL.createObjectURL(f) : null,
        status: 'extracting',
        text: '',
        error: '',
        // Raw File, kept ONLY on this composer object so a consented send can
        // persist documents for cross-device restore. Never copied onto the
        // sent message (useChatTurn strips to plain fields).
        file: f,
      };
    });
    setAttachments((prev) => [...prev, ...entries]);
    entries.forEach((e, i) => extractAttachment(e.id, take[i], e.fileType));
    if (picked.length > room) showToast(`Only ${room} more file(s) could be added`, 'info');
  };

  const removeAttachment = (id) => setAttachments((prev) => {
    const target = prev.find((a) => a.id === id);
    if (target?.previewUrl) URL.revokeObjectURL(target.previewUrl);
    return prev.filter((a) => a.id !== id);
  });

  const clearAttachments = () => setAttachments((prev) => {
    prev.forEach((a) => { if (a.previewUrl) URL.revokeObjectURL(a.previewUrl); });
    return [];
  });

  const onFilePick = (e) => { addAttachments(e.target.files); e.target.value = ''; };

  // Claude-style paste-to-attach: a pasted screenshot/file becomes an
  // attachment; plain text falls through to the default paste.
  const onComposerPaste = (e) => {
    const items = Array.from(e.clipboardData?.items || []);
    const files = items.filter((it) => it.kind === 'file').map((it) => it.getAsFile()).filter(Boolean);
    if (!files.length) return;
    const normalized = files.map((f) => {
      if (f.name && ATTACH_EXT.test(f.name)) return f;
      // Only nameless clipboard IMAGES get a synthetic filename. Renaming a
      // pasted .docx to .png made the backend's magic-byte check reject it.
      if (!String(f.type || '').startsWith('image/')) return f;
      const ext = f.type === 'image/jpeg' ? 'jpg' : 'png';
      return new File([f], `pasted-${uid()}.${ext}`, { type: f.type });
    });
    e.preventDefault();
    addAttachments(normalized); // its ext filter toasts anything unsupported
  };

  const previewAtt = attachments.find((a) => a.id === previewId) || null;

  // Rehydrate image thumbnails on restored conversations from the device-local
  // media cache, falling back to the server copy (the transcript keeps
  // attachment metadata only — never bytes). Same effect as the Agent page, so
  // a reopened studio chat shows its pictures instead of dead file chips.
  // Attempted ids are remembered so a cache miss doesn't loop.
  const mediaHydrateAttempted = useRef(new Set());
  useEffect(() => {
    const wanted = [];
    messages.forEach((m) => {
      if (m.role !== 'user' || !Array.isArray(m.attachments)) return;
      m.attachments.forEach((a) => {
        if (a.fileType === 'image' && !a.previewUrl && a.id && !mediaHydrateAttempted.current.has(a.id)) {
          mediaHydrateAttempted.current.add(a.id);
          wanted.push(a.id);
        }
      });
    });
    if (!wanted.length) return;
    (async () => {
      const found = new Map();
      for (const id of wanted) {
        // eslint-disable-next-line no-await-in-loop
        let dataUrl = await getChatImage(id);
        if (!dataUrl) {
          try {
            // eslint-disable-next-line no-await-in-loop
            const res = await aiAPI.getChatMedia(id);
            dataUrl = res?.data?.dataUrl || null;
            if (dataUrl) saveChatImage(id, dataUrl);
          } catch { /* not persisted (private chat / other user's device) */ }
        }
        if (dataUrl) found.set(id, dataUrl);
      }
      if (!found.size) return;
      setMessages((prev) => prev.map((m) => {
        if (m.role !== 'user' || !Array.isArray(m.attachments)) return m;
        if (!m.attachments.some((a) => found.has(a.id))) return m;
        return {
          ...m,
          attachments: m.attachments.map((a) => (found.has(a.id)
            ? { ...a, previewUrl: found.get(a.id), dataUrl: found.get(a.id) }
            : a)),
        };
      }));
    })();
  }, [messages, setMessages]);

  // Web search — OFF by default in the dock and there is deliberately NO
  // standing toggle: minutes drafting runs on the user's own facts + statutory
  // boilerplate. The capability stays one tap away — when Vee offers to verify
  // something on the web, the allow-card below flips this for the chat.
  const [webOn, setWebOn] = useState(false);

  // DC-1 session overrides — SAME localStorage mirror as AgentPage, so
  // corrections made in either surface ride every turn of this conversation.
  const [companyOverrides, setCompanyOverrides] = useState(() => {
    try { return JSON.parse(localStorage.getItem('cs_ai_company_overrides') || 'null') || {}; } catch { return {}; }
  });
  useEffect(() => {
    try { localStorage.setItem('cs_ai_company_overrides', JSON.stringify(companyOverrides)); } catch { /* ignore */ }
  }, [companyOverrides]);

  const { composeAndSend } = useChatTurn({ sendTurn, getPageContext });
  // The company THIS CONVERSATION is about, when the user picked one on the
  // company card — separate from the app's activeCompany, which only says where
  // the user is standing (the selector auto-pins the first company in the
  // portfolio). Derived from the transcript so a restored chat keeps its
  // binding without anything extra being persisted.
  const [threadCompanyCin, setThreadCompanyCin] = useState(null);
  useEffect(() => {
    setThreadCompanyCin(threadCompanyFrom(messages)?.cin || null);
  }, [messages]);

  const scrollRef = useRef(null);
  const endRef = useRef(null);
  const scrollToBottom = () => endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  useEffect(() => { setTimeout(scrollToBottom, 50); }, [messages.length]);

  // While this dock is mounted, IT owns the chat provider's page side effects.
  useEffect(() => registerPageHandlers({
    onClientActions: (actions) => { try { onClientActions?.(actions); } catch { /* host */ } },
    // set_company_details applies a correction with no confirmation card. Same
    // handler as AgentPage: without it the correction lives for exactly one
    // turn and the next message silently reverts to the stored record — the
    // studio docks share this dock, so they need it just as much.
    onCompanyOverrides: (updates) => setCompanyOverrides((prev) => {
      const next = { ...prev };
      for (const u of updates || []) {
        const cin = String(u?.companyCin || '').toUpperCase();
        if (!cin || !u?.values) continue;
        next[cin] = { ...(next[cin] || {}), ...u.values };
      }
      return next;
    }),
    onArtifact: (draft) => { try { onArtifact?.(draft); } catch { /* host */ } },
    onToast: showToast,
    onScroll: () => setTimeout(scrollToBottom, 0),
  }));

  const send = (text) => {
    const trimmed = String(text || '').trim();
    if (!trimmed) return;
    // Mid-generation → queue; flushes the moment the current reply finishes
    // (the quote chip stays and rides along with the flushed send).
    if (loading) {
      setQueuedText((prev) => (prev ? `${prev}\n${trimmed}` : trimmed));
      setInput('');
      return;
    }
    const ok = composeAndSend({
      text: trimmed,
      quotedSelection: quote || undefined,
      attachments,
      messages,
      activeCompanyCin: activeCompany?.cin || null,
      threadCompanyCin,
      webSearch: webOn,
      draftMode: (() => { try { return localStorage.getItem('cs_ai_draft_mode') || 'guided'; } catch { return 'guided'; } })(),
      companyOverrides,
      consent: { persist: consentPersist, train: consentTrain },
      sessionId,
    });
    if (ok) {
      setInput('');
      onClearQuote?.(); // consumed by this turn
      clearAttachments(); // consumed by this turn
    }
  };

  // Flush the queued prompt once the in-flight reply finishes (deferred a
  // tick so the provider's loading ref has synced — same as the Agent page).
  useEffect(() => {
    if (loading || !queuedText) return undefined;
    const q = queuedText;
    setQueuedText(null);
    const t = setTimeout(() => send(q), 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, queuedText]);

  // Host-initiated prompt. Goes through sendTyped, not send: every autoSend in
  // the app is a BUTTON the user pressed on the host page, so it is a
  // user-initiated send and must supersede a pending ask-card exactly as a typed
  // message does — otherwise Vee answers the button's instruction while the
  // stale card sits there, and the next turn re-asks around it.
  //
  // Call-site audit (the only four setAutoSend calls in the codebase, all
  // user-click handlers, none reachable from an ask-card's own flow):
  //   MinutesStudio.askVeeToFillGaps  · Details panel "let Vee fill the gaps"
  //   MinutesStudio.letVeeMerge       · conflict banner "keep mine, re-apply"
  //   ReportStudio.askVeeToFillGaps   · the report twin of the above
  //   ReportStudio.letVeeMerge        · the report twin of the above
  // A future host affordance that IS driven by a card (and therefore settles
  // that card itself) opts out with `{ text, supersede: false }` rather than
  // this effect being weakened for everyone.
  const autoSentRef = useRef(null);
  useEffect(() => {
    if (!autoSend || autoSentRef.current === autoSend) return;
    autoSentRef.current = autoSend;
    if (autoSend.supersede === false) send(autoSend.text || '');
    else sendTyped(autoSend.text || '');
    onAutoSent?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoSend]);

  // Shared context builder for the ported non-composeAndSend sends (edit-resend).
  const wireContext = () => ({
    platform: 'complyv',
    activeCompanyCin: activeCompany?.cin || null,
    ...(threadCompanyCin ? { threadCompanyCin } : {}),
    webSearch: webOn,
    draftMode: (() => { try { return localStorage.getItem('cs_ai_draft_mode') || 'guided'; } catch { return 'guided'; } })(),
    ...(Object.keys(companyOverrides).length ? { companyOverrides } : {}),
    ...(typeof getPageContext === 'function' ? (() => { const pc = getPageContext(); return pc ? { pageContext: pc } : {}; })() : {}),
  });

  // ── Origin-aware arrival ────────────────────────────────────────────────
  const consumedOpenRef = useRef(null);
  useEffect(() => {
    if (!openSessionId || consumedOpenRef.current === openSessionId) return;
    consumedOpenRef.current = openSessionId;
    openSession(openSessionId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openSessionId]);

  // ── In-dock history ─────────────────────────────────────────────────────
  const openHistoryView = () => {
    setView('history');
    if (!histSessions) setHistLoading(true);
    aiAPI.listChatSessions(historyDraftKind ? { draftKind: historyDraftKind } : {})
      .then((res) => {
        const list = res?.data?.sessions || res?.sessions || [];
        setHistSessions(Array.isArray(list) ? list : []);
      })
      .catch(() => { if (!histSessions) showToast('Could not load history', 'warning'); })
      .finally(() => setHistLoading(false));
  };

  const openSession = async (id, { quiet = false } = {}) => {
    if (id === sessionId && messages.some((m) => m.role === 'user')) {
      setView('chat');
      return;
    }
    try {
      const res = await aiAPI.getChatSession(id);
      const session = res?.data?.session || res?.session;
      const msgs = Array.isArray(session?.messages) ? session.messages : [];
      // A quiet resume is background continuity, not a user action: an empty or
      // deleted session just leaves a fresh chat, with nothing announced.
      if (!msgs.length) { if (quiet) resetChat(); else showToast('This conversation is empty', 'info'); return; }
      setMessages(hydratePersistedMessages(msgs));
      setSessionId(id);
      setConversationOrigin(session.origin || 'agent');
      setCompanyOverrides({});
      setView('chat');
      if (!quiet) showToast('Conversation restored', 'success');
      setTimeout(scrollToBottom, 80);
    } catch {
      // A quiet resume must never surface an error for something the user
      // didn't ask for — fall back to a fresh chat.
      if (quiet) resetChat(); else showToast('Could not open conversation', 'error');
    }
  };

  // ── Edit & resend with versions + branch (ported from the Agent page) ───
  const handleEditResend = (index, newText) => {
    if (loading) return;
    const original = messages[index];
    if (!original || original.role !== 'user') return;
    const base = messages.slice(0, index);
    const tail = messages.slice(index + 1).filter((m) => !m.loading && !m.recovering && !m.recoverFailed);
    const versions = (original.versions || [{ content: original.content, tail: null }]).slice();
    const curIdx = original.current ?? versions.length - 1;
    versions[curIdx] = { content: original.content, tail };
    versions.push({ content: newText, tail: null });
    const userMsg = { ...original, content: newText, versions, current: versions.length - 1 };
    const history = buildWireHistory([...base, userMsg]);
    setMessages(base);
    sendTurn({
      userMsg,
      history,
      context: wireContext(),
      consent: { persist: consentPersist, train: consentTrain },
      sessionId,
      userText: newText,
    });
  };

  const handleSwitchVersion = (index, targetIdx) => {
    if (loading) return;
    const msg = messages[index];
    const versions = (msg?.versions || []).slice();
    const curIdx = msg?.current ?? versions.length - 1;
    if (!versions[targetIdx] || targetIdx === curIdx) return;
    versions[curIdx] = { content: msg.content, tail: messages.slice(index + 1) };
    const target = versions[targetIdx];
    const restored = {
      ...msg,
      content: target.content,
      versions: versions.map((v, k) => (k === targetIdx ? { content: v.content, tail: null } : v)),
      current: targetIdx,
    };
    setMessages([...messages.slice(0, index), restored, ...(target.tail || [])]);
  };

  const handleBranchFrom = (index) => {
    if (loading) return;
    const slice = messages.slice(0, index + 1).map(({ versions, current, ...m }) => m);
    setMessages(slice);
    setSessionId(null);
    markForked(); // the saved copy gets a "[Fork]" mark in its name
    showToast('Branched into a new chat from this reply', 'success');
  };

  // ── Thumbs feedback (D-3, redacted server-side) ─────────────────────────
  // Download a reply as Word or PDF (the server sets out the as-on date and sources).
  const handleExport = async (msgIndex, format) => {
    const msg = messages[msgIndex];
    if (!msg || msg.role !== 'assistant') return;
    const heading = (msg.content.match(/^#{1,3}\s+(.+)$/m) || [])[1];
    const title = heading || msg.content.replace(/[#*_>`|-]/g, ' ').trim().split(/\s+/).slice(0, 8).join(' ');
    try {
      await aiAPI.exportAnswer({ format, message: msg.content, title });
    } catch {
      showToast('Could not build the file', 'warning');
    }
  };

  const handleFeedback = async (msgIndex, verdict, reasons) => {
    const msg = messages[msgIndex];
    if (!msg || msg.role !== 'assistant' || msg.feedback) return;
    let userText = '';
    for (let i = msgIndex - 1; i >= 0; i -= 1) {
      if (messages[i].role === 'user') { userText = messages[i].content; break; }
    }
    setMessages((prev) => prev.map((m, i) => (i === msgIndex ? { ...m, feedback: verdict } : m)));
    try {
      await aiAPI.sendChatFeedback({ sessionId, verdict, reasons, userText, assistantText: msg.content });
    } catch {
      showToast('Could not record feedback', 'warning');
    }
  };

  // ── Agent-action confirmation (the confirm→execute gate, server-side) ───
  const handleConfirmProposal = async (proposal) => {
    try {
      const res = await aiAPI.executeChatAction({
        action: proposal.action,
        params: proposal.params,
        idempotencyKey: proposal.idempotencyKey,
      });
      const result = res?.data?.result || res?.result || null;
      let toast = 'Done — the action was completed.';
      if (proposal.action === 'add_company') {
        switch (result?.outcome) {
          case 'created': toast = `Added ${result.name || 'company'} to your portfolio.`; break;
          case 'claimed': toast = `Claimed ${result.name || 'company'} — it's now yours.`; break;
          case 'access_requested': toast = 'Access request sent to the company owner.'; break;
          case 'already_has_access': toast = 'You already have access to this company.'; break;
          default: break;
        }
      }
      if (proposal.action === 'update_company_details') toast = 'Company details saved — drafts and registers will use them from now on.';
      showToast(toast, 'success');
      // Data-changing actions refresh the shared portfolio (same as the Agent page).
      if (['set_compliance_date', 'update_company_details', 'add_company'].includes(proposal.action) && typeof fetchCompanies === 'function') {
        await fetchCompanies().catch(() => {});
      }
      return true;
    } catch (e) {
      showToast(e?.message || 'Could not complete the action.', 'error');
      return e?.message || false;
    }
  };

  // Everything this conversation has already asked and had settled (T8.3).
  const askLedger = useMemo(() => buildAskLedger(messages), [messages]);

  // ── Pending ask (details → facts → placeholder fill → web offer) ────────
  const pendingAsk = useMemo(() => {
    const m = messages[messages.length - 1];
    if (!m || m.role !== 'assistant' || m.loading) return null;
    const idx = messages.length - 1;
    // An ask about a document this studio does not own (a resolution from the
    // Agent page landing in the Report dock) stays in the transcript but is
    // never PINNED here — pinning it would also hide this dock's composer.
    if (!askBelongsToSurface(askDraftKind(messages, idx), myPage)) return null;
    // A company confirmed anywhere in this thread is confirmed for good.
    const confirmed = confirmedCompanies(messages);
    const dc = (m.detailsConfirms || []).find((c) => !c.confirmed && !isCompanyConfirmSettled(c, confirmed));
    if (dc) return { type: 'confirm', msgIndex: idx, card: dc };
    // A card whose every question this thread has already settled is not an
    // ask — skip past it rather than showing the user an empty form.
    const fr = (m.factsRequests || []).find(
      (c) => !c.completed && pruneSettledFields(c.fields || [], askLedger).length,
    );
    // `fields` is carried SEPARATELY from `card`: fields Vee asked for as
    // `type: 'person'` gain this company's serving directors as picker options
    // and `type: 'company'` fields gain the user's own portfolio (only the
    // client knows either), but markCard matches the stored card by identity,
    // so the card reference itself must stay untouched.
    if (fr) {
      return {
        type: 'facts',
        msgIndex: idx,
        card: fr,
        fields: pruneSettledFields(
          attachCompanyOptions(attachPersonOptions(fr.fields, activeCompany), companies),
          askLedger,
        ),
      };
    }
    if (!m.placeholdersFilled && Array.isArray(m.drafts) && m.drafts.length) {
      const d = m.drafts[m.drafts.length - 1];
      // Placeholders this thread has already settled never come back: an
      // answered one is carried into the document by the effect below, a
      // skipped one was an explicit "leave it blank".
      const phs = pruneSettledFields(
        (d?.lint?.placeholders || []).map((ph) => ({ key: ph })),
        askLedger,
      ).map((f) => f.key);
      if (phs.length && !/fallback/i.test(d?.source || '')) {
        return {
          type: 'fill',
          msgIndex: idx,
          draft: d,
          card: {
            title: 'Fill in the remaining details',
            subtitle: 'These drop straight into the document — blanks stay as placeholders.',
            // Seed from the company record where the placeholder is
            // unambiguous — see placeholderPrefill for what is deliberately
            // left blank (director names/DINs, meeting time).
            fields: withPrefilledValues(
              phs.map((ph) => ({ key: ph, label: placeholderLabel(ph) })),
              {
                company: (!d.companyCin
                  || String(d.companyCin).toUpperCase() === String(activeCompany?.cin || '').toUpperCase())
                  ? activeCompany
                  : null,
                draft: d,
              },
            ),
          },
        };
      }
    }
    if (!webOn && !m.webOfferHandled && WEB_VERIFY_OFFER_RE.test(m.content || '')) {
      return { type: 'web', msgIndex: idx };
    }
    return null;
  }, [messages, webOn, activeCompany, askLedger, myPage]);

  const markCard = (msgIndex, listKey, card, patch) => {
    setMessages((prev) => prev.map((m, idx) => (
      idx === msgIndex
        ? { ...m, [listKey]: (m[listKey] || []).map((c) => (c === card ? { ...c, ...patch } : c)) }
        : m
    )));
  };

  // ── One ask, one answer ─────────────────────────────────────────────────
  // The composer stays live while a card is docked, so two paths can now reach
  // the same card inside one tick — a Submit click and a typed message that
  // supersedes it — and a card component holding a closure from an earlier
  // render could fire again after the card was already settled. Claim the ask
  // before acting on it: the first caller wins, every later one is a no-op.
  // Keyed on the object the ask lives on: the stored card for a facts/details
  // card, the DRAFT for the placeholder-fill form (whose card object is
  // synthesized fresh on every render, so it is useless as an identity).
  const handledAskRef = useRef(new WeakSet());
  const claimAsk = (key) => {
    if (!key || typeof key !== 'object') return true; // nothing to key on — don't block the user
    if (handledAskRef.current.has(key)) return false;
    handledAskRef.current.add(key);
    return true;
  };
  // Hand the claim back when the action turned out to be a no-op, so a form
  // whose values matched nothing in the document can still be retried.
  const releaseAsk = (key) => {
    if (key && typeof key === 'object') handledAskRef.current.delete(key);
  };

  const handleConfirmDetails = (changed, edited) => {
    if (!pendingAsk) return;
    const { card, msgIndex } = pendingAsk;
    if (!claimAsk(card)) return;
    if (edited && Object.keys(changed).length) {
      setCompanyOverrides((prev) => ({
        ...prev,
        [card.companyCin]: { ...(prev[card.companyCin] || {}), ...changed },
      }));
    }
    const hasPendingFacts = (messages[msgIndex]?.factsRequests || []).some((c) => !c.completed);
    markCard(msgIndex, 'detailsConfirms', card, { confirmed: true, confirmedWithEdits: edited, ackPending: hasPendingFacts });
    if (hasPendingFacts) return;
    const who = card.companyName || card.companyCin;
    send(edited
      ? `I corrected the company details for ${who} on the card — use the corrected values.`
      : `The company details for ${who} are correct — go ahead.`);
  };

  const takeDetailsAckLines = (msgIndex) => {
    const lines = [];
    (messages[msgIndex]?.detailsConfirms || []).forEach((c) => {
      if (c.confirmed && c.ackPending) {
        const who = c.companyName || c.companyCin;
        lines.push(c.confirmedWithEdits
          ? `I corrected the company details for ${who} on the card — use the corrected values.`
          : `The company details for ${who} are correct.`);
      }
    });
    if (lines.length) {
      setMessages((prev) => prev.map((m, i) => (i !== msgIndex ? m : {
        ...m,
        detailsConfirms: (m.detailsConfirms || []).map((c) => (c.ackPending ? { ...c, ackPending: false } : c)),
      })));
    }
    return lines;
  };

  const handleFactsSubmit = (values, otherText) => {
    if (!pendingAsk || !claimAsk(pendingAsk.card)) return;
    // `answers` is the thread's record of this question being settled — see
    // askLedger. Without it the same field is asked again next turn.
    markCard(pendingAsk.msgIndex, 'factsRequests', pendingAsk.card, { completed: true, answers: values });
    const ackLines = takeDetailsAckLines(pendingAsk.msgIndex);
    const lines = (pendingAsk.card.fields || [])
      .filter((f) => String(values[f.key] || '').trim())
      .map((f) => `${f.label}: ${String(values[f.key]).trim()}`);
    // A company pick binds the conversation, and its CIN goes into the message
    // so the binding lives in the transcript a restored chat can read back.
    const picked = pickedCompanyFrom(pendingAsk.card.fields, values);
    if (picked) setThreadCompanyCin(picked.cin);
    const text = [
      ...ackLines,
      ...(picked ? [companyChoiceSentence(picked)] : []),
      'Here are the details:',
      ...lines,
      otherText ? `Other: ${otherText}` : '',
    ].filter(Boolean).join('\n');
    send(text || 'Proceed with what you have.');
  };

  const handleFactsSkip = () => {
    if (!pendingAsk || !claimAsk(pendingAsk.card)) return;
    markCard(pendingAsk.msgIndex, 'factsRequests', pendingAsk.card, { completed: true, completedNote: 'skipped' });
    const ackLines = takeDetailsAckLines(pendingAsk.msgIndex);
    send([...ackLines, 'Skip the remaining questions — use placeholders where needed.'].join('\n'));
  };

  // Deterministic post-draft placeholder fill — exact client-side replacement,
  // no AI. The filled draft goes back onto the message AND to the host page
  // (canvas + DraftSession) via onArtifact.
  //
  // `values` is placeholder token → value. THE FORM IS THE FACT STORE (see
  // utils/factCarryForward.js): every value that lands in the document is also
  // written through to the open studio's form, so the next regeneration cannot
  // regress the fact back to a placeholder and re-ask for it.
  const applyPlaceholderValues = (draft, msgIndex, values, { quiet = false } = {}) => {
    // Shared with the AI Agent page and unit-tested — see applyPlaceholderFill.
    // These two surfaces carried near-identical copies of this loop, which is
    // one copy too many for the code that writes answers into a filing document.
    const { html, plainText, applied } = applyPlaceholderFill(draft, values);
    const filled = applied.length;
    if (!filled) return 0;
    // Write-through to the studio form, via the SAME client-action path a real
    // update_page_config tool call takes (bridge validation + field flash).
    if ((draft.kind || '') === 'minutes' && typeof onClientActions === 'function') {
      const patch = minutesPatchFromPlaceholders(values);
      if (Object.keys(patch).length) {
        try { onClientActions([{ type: 'patch_page_config', target: 'minutes', patch }]); } catch { /* host */ }
      }
    }
    const remaining = (draft.lint?.placeholders || []).filter((ph) => !String(values[ph] || '').trim());
    const updated = {
      ...draft,
      html,
      plainText,
      edited: true,
      lint: draft.lint ? {
        ...draft.lint,
        placeholders: remaining,
        findings: (draft.lint.findings || [])
          .filter((f) => f.code !== 'unfilled_placeholders')
          .concat(remaining.length ? [{
            code: 'unfilled_placeholders',
            severity: 'warning',
            message: `${remaining.length} placeholder${remaining.length === 1 ? '' : 's'} need real values before filing: ${remaining.join(', ')}.`,
          }] : []),
      } : draft.lint,
    };
    // A visible RECEIPT, because a deterministic fill produces no reply: the
    // form vanishes, the toast fades, and without this the only trace left in
    // the transcript is whatever the user typed in "Other" — so eight answered
    // fields read back as one stray note. A quiet auto-fill records nothing,
    // since the user did not answer anything.
    const receipt = quiet ? null : {
      title: 'Filled in the remaining details',
      completed: true,
      completedDetail: `${filled} detail${filled === 1 ? '' : 's'} added to the document`,
      fields: Object.keys(values).map((key) => ({ key, label: placeholderLabel(key) })),
      answers: values,
    };
    setMessages((prev) => prev.map((m, i) => {
      if (i !== msgIndex) return m;
      const next = {
        ...m,
        drafts: m.drafts.map((d) => (isSameDraft(d, draft) ? updated : d)),
        // The thread's record that these facts are settled (askLedger).
        filledPlaceholders: { ...(m.filledPlaceholders || {}), ...values },
        ...(receipt ? { factsRequests: [...(m.factsRequests || []), receipt] } : {}),
      };
      if (!remaining.length) next.placeholdersFilled = true;
      return next;
    }));
    try { onArtifact?.(updated); } catch { /* host */ }
    if (!quiet) showToast(`Filled ${filled} detail${filled === 1 ? '' : 's'} into the document`, 'success');
    return filled;
  };

  const handleFillPlaceholders = (values, otherText) => {
    if (!pendingAsk || pendingAsk.type !== 'fill') return;
    if (!claimAsk(pendingAsk.draft)) return;
    if (otherText) setQueuedText((prev) => (prev ? `${prev}\n${otherText}` : otherText));
    // A partial fill replaces the draft with a NEW object, so the next round of
    // the same form claims cleanly. Nothing filled = nothing settled: release.
    if (!applyPlaceholderValues(pendingAsk.draft, pendingAsk.msgIndex, values)) releaseAsk(pendingAsk.draft);
  };

  // Carry-forward: a regenerated draft that re-opened a question this thread
  // already answered is re-filled from the ledger instead of asking again.
  // Runs once per (message, draft) — applyPlaceholderValues clears the tokens
  // from lint.placeholders, so there is nothing left to match on a re-run.
  useEffect(() => {
    const idx = messages.length - 1;
    const m = messages[idx];
    if (!m || m.role !== 'assistant' || m.loading || m.placeholdersFilled) return;
    const d = Array.isArray(m.drafts) && m.drafts.length ? m.drafts[m.drafts.length - 1] : null;
    if (!d || /fallback/i.test(d.source || '')) return;
    const known = carryForwardValues(d.lint?.placeholders || [], askLedger);
    if (!Object.keys(known).length) return;
    applyPlaceholderValues(d, idx, known, { quiet: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, askLedger]);

  const handleDismissFill = () => {
    if (!pendingAsk || !claimAsk(pendingAsk.draft)) return;
    // Remember WHICH questions were declined, not just that the card was
    // closed — otherwise the next draft re-opens every one of them.
    const declined = (pendingAsk.card?.fields || []).map((f) => f.key);
    setMessages((prev) => prev.map((m, i) => (i === pendingAsk.msgIndex ? {
      ...m,
      placeholdersFilled: true,
      dismissedPlaceholders: [...new Set([...(m.dismissedPlaceholders || []), ...declined])],
    } : m)));
  };

  // QA-3.2 one-tap web-verify consent: flip the toggle, re-ask via the queue
  // so the outgoing context reads the committed value.
  const handleAllowWebSearch = (msgIndex) => {
    let userText = '';
    for (let i = msgIndex - 1; i >= 0; i -= 1) {
      if (messages[i].role === 'user') { userText = messages[i].content; break; }
    }
    setMessages((prev) => prev.map((m, i) => (i === msgIndex ? { ...m, webOfferHandled: true } : m)));
    setWebOn(true);
    if (userText) setQueuedText((prev) => (prev ? `${prev}\n${userText}` : userText));
  };

  const handleDismissWebOffer = (msgIndex) => {
    setMessages((prev) => prev.map((m, i) => (i === msgIndex ? { ...m, webOfferHandled: true } : m)));
  };

  // ── A typed message supersedes the pending ask-card (see askLedger.js) ───
  // The composer is never locked, so the user can answer in prose instead of in
  // the form. The message wins and the card is settled as SKIPPED — the exact
  // record Skip leaves — so the ledger never re-opens those questions and the
  // card cannot come back later to splice stale answers into a document the
  // newer instruction already changed.
  //
  // Returns any confirmation lines the settled card still owed, to ride in
  // front of the user's text (same contract as handleFactsSkip).
  const supersedePendingAsk = () => {
    if (!pendingAsk) return [];
    if (pendingAsk.type === 'facts') {
      if (!claimAsk(pendingAsk.card)) return [];
      markCard(pendingAsk.msgIndex, 'factsRequests', pendingAsk.card, supersededByChatPatch());
      return takeDetailsAckLines(pendingAsk.msgIndex);
    }
    if (pendingAsk.type === 'fill') {
      if (!claimAsk(pendingAsk.draft)) return [];
      const declined = (pendingAsk.card?.fields || []).map((f) => f.key);
      setMessages((prev) => prev.map((m, i) => (i !== pendingAsk.msgIndex ? m : {
        ...m,
        placeholdersFilled: true,
        dismissedPlaceholders: [...new Set([...(m.dismissedPlaceholders || []), ...declined])],
        // The fill form is synthesized from the draft's own placeholders, so
        // there is no stored card to turn into a receipt. Record one, so the
        // transcript says what happened instead of the form just vanishing.
        factsRequests: [...(m.factsRequests || []), {
          title: 'Fill in the remaining details',
          fields: declined.map((key) => ({ key, label: placeholderLabel(key) })),
          ...supersededByChatPatch(),
        }],
      })));
      return [];
    }
    // 'confirm' — never auto-confirmed: see askLedger.js. 'web' — an offer, not
    // a question; it retires itself on the next reply. Both stay put.
    return [];
  };

  // Every user-INITIATED send goes through here: the composer (Enter + the send
  // button) and the host page's autoSend buttons. The supersede rule applies to
  // what the user chose to send — never to the synthetic sends the ask handlers
  // make themselves (handleFactsSubmit / handleFactsSkip / handleConfirmDetails),
  // which call `send` directly because they have already settled their own card
  // and re-entering the supersede would fight the claim guard.
  const sendTyped = (text) => {
    const trimmed = String(text || '').trim();
    if (!trimmed) return;
    const ack = supersedePendingAsk();
    send(ack.length ? [...ack, trimmed].join('\n') : trimmed);
  };

  // Does a typed message actually displace what's docked? True only for the
  // cards the supersede rule settles — so the composer hint never over-promises.
  const askIsSuperseding = !!pendingAsk && (pendingAsk.type === 'facts' || pendingAsk.type === 'fill');

  return (
    // No side border of its own — the host frame owns the outline.
    <div className="flex flex-col h-full min-h-0 bg-white">
      {/* Dock header */}
      <div className="flex items-center gap-2 px-3 py-2 border-b shrink-0" style={{ borderColor: '#e7e0d0', background: '#faf8f2' }}>
        <img src="/vee-mascot.svg" alt="" className="w-5 h-5 shrink-0" />
        <span className="text-[12px] font-bold text-[#08111f] whitespace-nowrap shrink-0">{title}</span>
        <span className="text-[9.5px] text-[#9a9080] truncate min-w-0">· same chat as the Vee page</span>
        <span className="ml-auto flex items-center gap-0.5 shrink-0">
          <button
            type="button"
            onClick={() => {
              resetChat();
              setCompanyOverrides({});
              // A deliberate fresh start also clears this surface's resume
              // memory — otherwise the old chat would reopen on the next visit.
              if (myPage) { try { localStorage.removeItem(lastSessionKey(myPage)); } catch { /* ignore */ } }
              setView('chat'); // New chat means chatting — leave the History list
              showToast('Started a new chat — the previous one is in History', 'success');
            }}
            className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 h-6 rounded-md text-[#5a5145] hover:bg-[#f0ece2] transition-colors active:scale-95"
          >
            <FiPlus size={12} /> New chat
          </button>
          <button
            type="button"
            onClick={() => (view === 'history' ? setView('chat') : openHistoryView())}
            title={historyDraftKind ? `Conversations that drafted ${historyDraftKind}` : 'Chat history'}
            className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 h-6 rounded-md transition-colors active:scale-95"
            style={view === 'history' ? { background: '#145f5f', color: '#fff' } : { color: '#5a5145' }}
          >
            <FiClock size={12} /> History
          </button>
          {onClose && (
            <button type="button" onClick={onClose} aria-label="Close copilot" className="w-6 h-6 rounded-md flex items-center justify-center text-[#9a9080] hover:bg-[#f0ece2]">
              <FiX size={14} />
            </button>
          )}
        </span>
      </div>

      {/* ── In-dock history ── */}
      {view === 'history' && (
        <div className="flex-1 min-h-0 flex flex-col">
          <div className="flex items-center gap-2 px-3 py-2 shrink-0">
            <button
              type="button"
              onClick={() => setView('chat')}
              className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 h-6 rounded-md text-[#5a5145] hover:bg-[#f0ece2]"
            >
              <FiArrowLeft size={12} /> Back to chat
            </button>
            <span className="text-[10px] uppercase tracking-[0.5px] font-bold ml-auto" style={{ color: '#9a9080' }}>
              {historyDraftKind ? `${historyDraftKind} conversations` : 'Conversations'}
            </span>
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto cs-content-scroll px-2 pb-2 space-y-1.5">
            {histLoading && !histSessions ? (
              <div className="text-[12px] text-[#9a9080] px-2 py-6 text-center">Loading…</div>
            ) : (histSessions || []).length === 0 ? (
              <div className="text-[12px] text-[#9a9080] px-3 py-6 text-center leading-relaxed">
                No {historyDraftKind || ''} conversations yet — chats that draft
                {historyDraftKind === 'minutes' ? ' minutes' : historyDraftKind === 'report' ? ' reports' : ' documents'} will appear here.
              </div>
            ) : (
              (histSessions || []).map((s) => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => openSession(s.id)}
                  className={`w-full text-left rounded-xl border bg-white px-3 py-2 transition-colors hover:border-[#145f5f] ${s.id === sessionId ? 'border-[#145f5f] ring-1 ring-[rgba(20,95,95,0.25)]' : ''}`}
                  style={s.id === sessionId ? undefined : { borderColor: '#e7e0d0' }}
                >
                  <div className="text-[12px] font-semibold text-[#08111f] truncate">{s.title || 'Untitled conversation'}</div>
                  <div className="flex items-center gap-2 text-[10px] mt-0.5" style={{ color: '#9a9080' }}>
                    <span>{timeAgo(s.updatedAt)}</span>
                    {s.docCount > 0 && (
                      <span className="inline-flex items-center gap-1"><FiFileText size={10} /> {s.docCount} doc{s.docCount === 1 ? '' : 's'}</span>
                    )}
                    {s.id === sessionId && <span className="font-semibold" style={{ color: '#145f5f' }}>current</span>}
                  </div>
                </button>
              ))
            )}
          </div>
          <button
            type="button"
            onClick={() => navigate(ROUTES.AGENT_HISTORY)}
            className="shrink-0 inline-flex items-center justify-center gap-1.5 text-[11px] font-semibold h-8 mx-2 mb-2 rounded-lg border transition-colors hover:bg-[#f6f3ea]"
            style={{ borderColor: '#e7e0d0', color: '#5a5145' }}
          >
            All conversations <FiExternalLink size={11} />
          </button>
        </div>
      )}

      {/* Transcript */}
      <div ref={scrollRef} className={`flex-1 min-h-0 overflow-y-auto cs-content-scroll ${view === 'history' ? 'hidden' : ''}`}>
        {messages.length <= 1 ? (
          <div className="px-4 py-6 text-[12px] leading-relaxed text-[#7a7060]">
            {emptyHint || (
              <>
                Tell Vee about the meeting in plain words — <i>“board meeting on 15 July, 3 to 4:30 pm,
                Rakesh chaired, agenda: HDFC current account”</i> — the form fills itself, and Vee
                drafts the minutes right here when you say so.
              </>
            )}
          </div>
        ) : (
          <ChatThread
            compact
            messages={messages}
            endRef={endRef}
            onCopy={(c) => { navigator.clipboard?.writeText(c); showToast('Copied', 'success'); }}
            onOpenArtifact={(d) => { try { onArtifact?.(d); } catch { /* host */ } }}
            activeArtifact={null}
            // In a studio the document is open beside this dock, so its own
            // drafts get no "Open" card — myPage is 'minutes' or 'report'
            // there, and null on the standalone agent page where the card is
            // the only way in.
            inlineDocKind={myPage}
            onConfirmProposal={handleConfirmProposal}
            onFeedback={handleFeedback}
            onExport={handleExport}
            onEditResend={handleEditResend}
            onSwitchVersion={handleSwitchVersion}
            onBranchFrom={handleBranchFrom}
            onNavigateSource={navigate}
            onOpenAttachment={(m, a) => setSentPreview({ items: m.attachments, id: a.id })}
            canEdit={!loading}
          />
        )}
      </div>

      {/* Pending ask-card docks above the composer (Claude-style) */}
      {view === 'chat' && pendingAsk && (
        <div className="border-t max-h-[45%] overflow-y-auto cs-content-scroll shrink-0" style={{ borderColor: '#e7e0d0' }}>
          {pendingAsk.type === 'confirm' ? (
            <DetailsConfirmCard flat card={pendingAsk.card} onConfirm={handleConfirmDetails} disabled={loading} />
          ) : pendingAsk.type === 'facts' ? (
            <FactsFormCard key={`facts:${pendingAsk.msgIndex}`} flat card={{ ...pendingAsk.card, fields: pendingAsk.fields }} onSubmit={handleFactsSubmit} onSkip={handleFactsSkip} disabled={loading} />
          ) : pendingAsk.type === 'fill' ? (
            // Keyed so a facts→fill transition remounts: the card seeds its
            // prefilled values on mount only (see placeholderPrefill).
            // The field list also keys it: carry-forward can shrink the form
            // after mount, and the card seeds its state on mount only.
            <FactsFormCard key={`fill:${pendingAsk.msgIndex}:${(pendingAsk.card.fields || []).map((f) => f.key).join('|')}`} flat card={pendingAsk.card} onSubmit={handleFillPlaceholders} onSkip={handleDismissFill} disabled={loading} />
          ) : (
            <WebSearchOfferCard
              flat
              onAllow={() => handleAllowWebSearch(pendingAsk.msgIndex)}
              onDismiss={() => handleDismissWebOffer(pendingAsk.msgIndex)}
            />
          )}
        </div>
      )}

      {/* Quoted document passage — SAME chip design as the AI Agent page's
          (icon tile + "Selected from the draft" label + expandable excerpt). */}
      {view === 'chat' && quote && (
        <div className="mx-3 mt-2.5 mb-2.5 rounded-xl border px-3 py-2 shrink-0" style={{ borderColor: 'rgba(20,95,95,0.35)', background: 'rgba(20,95,95,0.05)' }}>
          <div className="flex items-start gap-2.5">
            <div className="w-6 h-6 rounded-md flex items-center justify-center shrink-0 mt-0.5" style={{ background: 'rgba(20,95,95,0.1)', color: '#145f5f' }}>
              <FiFileText size={12} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[9.5px] uppercase tracking-[0.6px] font-semibold text-[#145f5f]">Selected from the document</div>
              <button
                type="button"
                onClick={() => setQuoteExpanded((v) => !v)}
                title={quoteExpanded ? 'Collapse' : 'Click to see the full selection'}
                className="block w-full text-left text-[12px] leading-snug text-[#3f4b5e] mt-0.5 cursor-pointer"
                style={quoteExpanded
                  ? { maxHeight: 180, overflowY: 'auto', whiteSpace: 'pre-wrap' }
                  : { display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
              >
                {quote}
              </button>
            </div>
            <button
              type="button"
              onClick={() => { setQuoteExpanded(false); onClearQuote?.(); }}
              className="w-6 h-6 rounded-md flex items-center justify-center text-[#9a9080] hover:text-[#08111f] hover:bg-[#f0ece2] transition-colors shrink-0"
              aria-label="Remove selection"
            >
              <FiX size={13} />
            </button>
          </div>
        </div>
      )}

      {/* Attachment chips (extracting/ready/error) — click to preview */}
      {view === 'chat' && attachments.length > 0 && (
        <div className="flex flex-wrap gap-2 mx-3 mt-2.5 mb-2.5 shrink-0">
          {attachments.map((a) => (
            <AttachmentChip key={a.id} att={a} onRemove={removeAttachment} onOpen={(att) => setPreviewId(att.id)} />
          ))}
        </div>
      )}

      {/* Composer (attach · type · send) — ALWAYS rendered, and never disabled
          by a docked ask-card. Answering the card and typing a message are
          independent paths into the same conversation: a user who wants to
          change course ("forget the bank details, draft the DIR-12 instead")
          must be able to say so without first submitting or skipping a form
          about something they've abandoned. The typed message then supersedes
          the card — see supersedePendingAsk / askLedger.js. Only the History
          view hides it, because there is no conversation to type into there. */}
      <div className={`px-2.5 pt-2.5 pb-2 border-t shrink-0 ${view === 'history' ? 'hidden' : ''}`} style={{ borderColor: '#e7e0d0' }}>
        <input ref={fileInputRef} type="file" accept={ATTACH_ACCEPT} multiple className="hidden" onChange={onFilePick} />
        <ChatComposer
          value={input}
          onChange={setInput}
          onSend={sendTyped}
          onStop={stopGenerating}
          loading={loading}
          onPaste={onComposerPaste}
          leftAccessory={(
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={attachments.length >= MAX_ATTACHMENTS}
              title="Attach a file — image, PDF, TXT or DOCX (e.g. a signed resolution)"
              aria-label="Attach a file"
              className="w-8 h-8 rounded-lg shrink-0 flex items-center justify-center text-[#9a9080] hover:text-[#145f5f] hover:bg-[#f0ece2] transition-colors disabled:opacity-30"
            >
              <FiPaperclip size={14} />
            </button>
          )}
          placeholder={queuedText
            ? 'Queued — sends next…'
            : askIsSuperseding
              // States the rule at the moment it matters, so the user isn't
              // guessing whether typing abandons the form above.
              ? 'Or type here instead — this skips the questions above'
              : (composerPlaceholder || 'Describe the meeting…')}
        />
        <div className="flex items-center gap-2 px-1 pt-2 pb-0.5">
          {/* Web search shows only when it's actually ON (via Vee's one-tap
              verify offer) — a quiet state indicator, not standing chrome. */}
          {webOn && (
            <button
              type="button"
              onClick={() => setWebOn(false)}
              title="Web search is ON for this chat — click to turn it off"
              className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 h-5 rounded-full border transition-colors"
              style={{ background: 'rgba(20,95,95,0.1)', borderColor: 'rgba(20,95,95,0.35)', color: '#145f5f' }}
            >
              <FiGlobe size={10} /> Web on
            </button>
          )}
          {queuedText && <span className="text-[10px]" style={{ color: '#9a7a3a' }}>1 message queued</span>}
          <span className="ml-auto text-[9.5px]" style={{ color: '#a09880' }}>Vee can make mistakes — verify before filing.</span>
        </div>
      </div>

      {/* Attachment lightbox (zoom/pan for images, extracted text for docs) */}
      {previewAtt && (
        <AttachmentPreview
          items={attachments}
          currentId={previewId}
          onSelect={setPreviewId}
          onClose={() => setPreviewId(null)}
          onRemove={removeAttachment}
        />
      )}

      {/* Same lightbox over a sent message's attachments — no Remove (history) */}
      {sentPreview && (
        <AttachmentPreview
          items={sentPreview.items}
          currentId={sentPreview.id}
          onSelect={(id) => setSentPreview((p) => (p ? { ...p, id } : p))}
          onClose={() => setSentPreview(null)}
        />
      )}
    </div>
  );
}
